"use client"

import { useState, useEffect } from 'react';
import {
  TrendingUp,
  Users,
  DollarSign,
  FileText,
  ArrowUpRight,
  ArrowDownRight,
  MoreHorizontal,
  Eye,
  Calendar,
  Clock,
  CheckCircle,
  AlertCircle,
  XCircle,
  Plus,
  Activity,
  Zap,
  X,
  Lock,
  Cloud,
  Hospital,
  Files,
  Landmark,
  IndianRupee
} from "lucide-react";
import axios from 'axios';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { motion } from "framer-motion";
import { getFounders, getUserSubscriptions, postMeeting, getInterests, createCloudCreditApplication, uploadFile, createDocumentation, dynamicFieldsAPI, getFundingRequests, createHRapplication } from '../utils/api';
import { toast } from 'react-toastify';
import RevenueOverview from '../components/RevenueChart';
import { FaPlus } from 'react-icons/fa';
import { isGuest } from "../utils/isGuest"

const CountdownTimer = ({ targetDate, label, offsetDays = 0 }) => {
  // ensure we work with a Date object
  const target = targetDate ? new Date(targetDate) : null;

  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });

  useEffect(() => {
    if (!target || isNaN(target.getTime())) {
      // invalid date
      setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
      return;
    }

    let mounted = true;
    const offsetMs = offsetDays * 24 * 60 * 60 * 1000;

    const tick = () => {
      const now = new Date();
      // subtract offsetDays by reducing the time remaining
      const rawDiff = target.getTime() - now.getTime() - offsetMs;
      const diff = Math.max(0, rawDiff); // never negative

      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      if (mounted) {
        setTimeLeft({ days, hours, minutes, seconds });
      }
    };

    // run immediately so UI isn't blank for 1s
    tick();
    const id = setInterval(tick, 1000);

    return () => {
      mounted = false;
      clearInterval(id);
    };
  }, [targetDate, offsetDays]); // updates if target or offset changes

  return (
    <div className="mt-2">
      {label && <div className="text-xs text-gray-600 mb-1">{label}</div>}
      <div className="flex items-center gap-1">
        <div className="bg-white/70 px-1.5 py-0.5 rounded text-xs font-bold">
          {timeLeft.days}d
        </div>
        <div className="text-xs">:</div>
        <div className="bg-white/70 px-1.5 py-0.5 rounded text-xs font-bold">
          {String(timeLeft.hours).padStart(2, '0')}h
        </div>
        <div className="text-xs">:</div>
        <div className="bg-white/70 px-1.5 py-0.5 rounded text-xs font-bold">
          {String(timeLeft.minutes).padStart(2, '0')}m
        </div>
        <div className="text-xs">:</div>
        <div className="bg-white/70 px-1.5 py-0.5 rounded text-xs font-bold">
          {String(timeLeft.seconds).padStart(2, '0')}s
        </div>
      </div>
    </div>
  );
};

function ScheduleMeetingModal({ onClose, onMeetingScheduled }) {
  const [activeForm, setActiveForm] = useState('consultation')
  const [selectedDate, setSelectedDate] = useState(null)
  const [meetingMode, setMeetingMode] = useState('online')
  const [submitted, setSubmitted] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [userData, setUserData] = useState({
    name: '',
    email: ''
  })

  // Fetch user data from localStorage on component mount
  useEffect(() => {
    const userName = localStorage.getItem('startupName') || '';
    const userEmail = localStorage.getItem('email') || '';

    setUserData({
      name: userName,
      email: userEmail
    });
  }, []);

  const handleDateChange = date => {
    setSelectedDate(date)
    setErrorMessage('')
  }

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate required fields
    if (!selectedDate) {
      setErrorMessage('Please select a date');
      return;
    }

    if (!userData.name || !userData.email) {
      setErrorMessage('User information is missing. Please try again later.');
      return;
    }

    setIsSubmitting(true);
    setErrorMessage('');

    try {
      // Prepare meeting data with all required fields
      const meetingData = {
        name: userData.name,
        email: userData.email,
        meetingType: activeForm === 'consultation' ? 'consultation' : 'ideation',
        meetingDate: selectedDate.toISOString(),
        meetingMode: meetingMode,
        userId: localStorage.getItem('startupId')
      };

      // Call your API to schedule the meeting
      const response = await postMeeting(meetingData);

      // If successful, show success state
      setSubmitted(true);

      // Notify parent component if needed
      if (onMeetingScheduled) {
        onMeetingScheduled();
      }
    } catch (error) {
      console.error('Error scheduling meeting:', error);
      setErrorMessage(error.response?.data?.error || 'Failed to schedule meeting. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="p-4 max-w-sm mx-auto bg-white rounded-xl shadow-lg text-sm">
        <div className="text-center py-6">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-2">
            <CheckCircle className="w-6 h-6 text-green-500" />
          </div>
          <h2 className="text-xl font-semibold text-green-600 mb-1">Meeting Scheduled!</h2>
          <p className="text-gray-600 mb-1">
            Your {activeForm === 'consultation' ? 'Business Mentorship' : 'Business Ideation'} meeting is set for:
          </p>
          <div className="bg-gray-50 p-2 rounded mb-4">
            <p className="font-medium text-gray-800 text-sm">
              {selectedDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
            </p>
            <p className="text-xs text-gray-500 mt-1">
              {meetingMode === 'online' ? 'Online Meeting' : 'In-Person Meeting'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-full py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
          >
            Close
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="p-4 max-w-sm mx-auto bg-white rounded-xl text-sm">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold text-gray-800">Schedule Meeting</h2>
        <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Toggle Buttons */}
      <div className="flex justify-center mb-4">
        <div className="inline-flex rounded-md shadow-sm" role="group">
          <button
            type="button"
            onClick={() => setActiveForm('consultation')}
            className={`px-3 py-1 text-xs font-medium rounded-l border ${activeForm === 'consultation'
              ? 'bg-blue-600 text-white border-blue-600'
              : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
          >
            Mentorship
          </button>
          <button
            type="button"
            onClick={() => setActiveForm('ideation')}
            className={`px-3 py-1 text-xs font-medium rounded-r border ${activeForm === 'ideation'
              ? 'bg-yellow-600 text-white border-yellow-600'
              : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
          >
            Ideation
          </button>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Date Picker */}
        <div className="flex justify-center overflow-hidden">
          <div className="w-full max-w-xs">
            <label className="block text-xs font-medium text-gray-700 mb-1 -ml-4">
              Select Date *
            </label>
            <div className="flex justify-center">
              <DatePicker
                selected={selectedDate}
                onChange={handleDateChange}
                minDate={new Date()}
                dateFormat="MMM d, yyyy"
                inline
                className="overflow-hidden"
                calendarClassName="overflow-hidden scrollbar-none flex justify-center"
              />
            </div>
          </div>
        </div>

        {/* Meeting Mode */}
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Mode *</label>
          <div className="grid grid-cols-2 gap-2">
            {['online', 'physical'].map(mode => (
              <button
                key={mode}
                type="button"
                onClick={() => setMeetingMode(mode)}
                className={`py-2 px-3 text-xs rounded border transition ${meetingMode === mode
                  ? 'bg-blue-50 border-blue-500 text-blue-600'
                  : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
              >
                {mode.charAt(0).toUpperCase() + mode.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {errorMessage && (
          <div className="bg-red-50 border border-red-200 text-red-600 p-2 rounded text-center text-xs">
            {errorMessage}
          </div>
        )}

        <button
          type="submit"
          className={`w-full py-2 text-xs font-medium rounded text-white transition ${!selectedDate
            ? 'bg-gray-400 cursor-not-allowed'
            : activeForm === 'consultation'
              ? 'bg-blue-600 hover:bg-blue-700'
              : 'bg-yellow-600 hover:bg-yellow-700'
            }`}
          disabled={!selectedDate || isSubmitting}
        >
          {isSubmitting ? 'Scheduling...' : 'Confirm Meeting'}
        </button>
      </form>
    </div>
  )
}

const fetchActivities = async (limit) => {
  const userId = localStorage.getItem('startupId');
  const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/recent-activities`, {
    params: { userId, limit }
  });
  return response.data || [];
};

const fetchStatsData = async () => {
  const token = localStorage.getItem('token');
  const startupId = localStorage.getItem('startupId');
  const fundingId = localStorage.getItem('fundingId');

  const norm = (res) =>
    Array.isArray(res) ? res :
      Array.isArray(res?.data) ? res.data :
        Array.isArray(res?.items) ? res.items : [];

  // Protect individual calls so one failing API doesn't break the whole stats fetch
  const foundersPromise = getFounders(startupId).catch(err => {
    console.error('getFounders failed', err);
    return [];
  });

  const fundingRequestsPromise = getFundingRequests(startupId).catch(err => {
    console.error('getFundingRequests failed', err);
    return [];
  });

  // Only request interests when we have a fundingId to query
  const interestsPromise = fundingId ? getInterests(fundingId).catch(err => {
    console.error('getInterests failed', err);
    return [];
  }) : Promise.resolve([]);

  const subscriptionsPromise = getUserSubscriptions(token).catch(err => {
    console.error('getUserSubscriptions failed', err);
    return [];
  });

  const [foundersResponse, fundingRequestsResponse, interestsResponse, subscriptionsResponse] = await Promise.all([
    foundersPromise,
    fundingRequestsPromise,
    interestsPromise,
    subscriptionsPromise
  ]);

  const foundersAll = norm(foundersResponse);
  const foundersData = foundersAll;
  const activeFoundersCount = foundersData.length;

  const fundingAll = norm(fundingRequestsResponse);
  const fundingData = fundingAll.filter(fr => {
    const candidates = [
      fr.startupId, fr.userId, fr.ownerId,
      fr.startup?.id, fr.startup?._id, fr.user?._id
    ].filter(Boolean);
    return candidates.includes(startupId);
  });
  const fundingRequestsCount = fundingData.length;

  const interestsData = norm(interestsResponse);
  const interestsCount = interestsData.length;

  const subsArray = Array.isArray(subscriptionsResponse) ? subscriptionsResponse : [];
  const hasCRM = subsArray.some(s => s.crmIncluded === true);

  let applicationsCount = 0;
  try {
    const jobId = 'some-default-id';
    const appsResponse = await axios.get(`${process.env.REACT_APP_API_URL}/api/jobapplied/${jobId}`);
    applicationsCount = appsResponse.data?.length || 0;
  } catch (error) {
    console.error("Error fetching applications:", error);
  }

  return {
    founders: activeFoundersCount,
    applications: applicationsCount,
    fundingRequests: fundingRequestsCount,
    hasCRM,
    foundersData,
    interestsData
  };
};


export default function ModernDashboard() {
  const [loading, setLoading] = useState(true);
  const [showAllActivities, setShowAllActivities] = useState(false);
  const [showMeetingModal, setShowMeetingModal] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isQuotaExceeded, setIsQuotaExceeded] = useState(false)


  const [statsData, setStatsData] = useState({
    founders: 0,
    applications: 0,
    fundingRequests: 0,
    hasCRM: false,
    loading: true
  });

  const [showCloudCreditsModal, setShowCloudCreditsModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [cloudCreditsForm, setCloudCreditsForm] = useState({
    organisationLegalName: "",
    registeredAddress: "",
    dpittCertificate: null,
    spocName: "",
    spocEmail: "",
    spocPhone: "",
    organisationAge: "",
    hasAvailedCredits: false,
    cloudProvider: "",
    currentlyOnCredits: false,
    creditsRemaining: "",
    receivedFunding: false,
    fundingAmount: ""
  });
  const [documentationForm, setDocumentationForm] = useState({
    startupName: '',
    email: '',
    phone: '',
    industry: '',
    location: '',
    pitchDeck: null,
    route: 'general',
    dynamicFields: {}
  });
  const [showHrModal, setShowHrModal] = useState(false)
  const [showDocumentationModal, setShowDocumentationModal] = useState(false);
  const [isUploadingPitchDeck, setIsUploadingPitchDeck] = useState(false);
  const [pitchDeckUrl, setPitchDeckUrl] = useState('');
  const [dynamicFields, setDynamicFields] = useState([]);
  const [loadingDynamicFields, setLoadingDynamicFields] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [showHospitalityModal, setShowHospitalityModal] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [selectedCoupon, setSelectedCoupon] = useState(null);
  const [coupons, setCoupons] = useState([]);
  const [loadingCoupons, setLoadingCoupons] = useState(true);
  const [usedCoupens, setUsedCoupens] = useState([])
  const [hrData, setHrData] = useState({
    companyName: "",
    GSTno: "",
    email: "",
    phone: "",
    contactperson: "",
    companyAddress: "",
    website: "",
    jobTitle: "",
    department: "",
    postions: "",
    jobType: "",
    jobLocation: "",
    workingDays: "",
    workingHours: "",
    salary: "",
    incentives: "",
    skills: [],
    benefits: [],
    qualification: "",
    experience: "",
    languages: "",
    gender: "",
    age: "",
    additionalNote: "",
    companyID: ""
  })

  useEffect(() => {
    console.log(hrData)
  }, [hrData])


  const fetchUserCoupons = async () => {
    try {
      setLoadingCoupons(true);
      const startupId = localStorage.getItem('startupId');
      const token = localStorage.getItem('Startuptoken');

      if (isGuest()) {
        return;
      }

      if (!startupId || !token) {
        console.error('Missing startupId or token');
        setCoupons([]);
        setLoadingCoupons(false);
        return;
      }

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/${startupId}/coupons`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      )

      setIsQuotaExceeded(response.isQuotaExceeded)

      console.log('Coupons API response:', response.data);

      // Handle different possible response structures
      let couponsData = [];

      if (response.data && Array.isArray(response.data)) {
        couponsData = response.data;
      } else if (response.data && response.data.coupons) {
        couponsData = response.data.coupons;
      } else if (response.data && response.data.data) {
        couponsData = response.data.data;
      } else {
        console.error('Unexpected API response structure:', response.data);
        throw new Error('Unexpected API response structure');
      }



      // Filter for hospitality/transport coupons and map to expected format
      const hospitalityCoupons = couponsData
        .filter(coupon => coupon && coupon.code)
        .map(coupon => ({
          _id: coupon._id || coupon.id,
          code: coupon.code,
          discount: coupon.discount || 'Special Discount',
          provider: coupon.provider || 'Service Provider',
          expires: coupon.expires,
          used: coupon.status === 'used' || coupon.used === true,
          status: coupon.status || 'active'
        }));
      const alreadyUsedCoupens = couponsData.filter((each) => each.status == "used")
      setUsedCoupens(alreadyUsedCoupens)

      setCoupons(hospitalityCoupons);

    } catch (error) {
      console.error('Error fetching coupons:', error);
      // Set empty array instead of sample data for production
      setCoupons([]);

      // Show error message to user
      if (error.response?.status === 401) {
        toast.error('Please login again to access coupons');
      } else if (error.response?.status === 404) {
        toast.error('Coupons service is currently unavailable');
      } else {
        toast.error('Failed to load coupons. Please try again later.');
      }
    } finally {
      setLoadingCoupons(false);
    }
  };

  const onCopyClick = async (coupen) => {
    console.log(coupen)
    try {
      await navigator.clipboard.writeText(coupen.code);
      console.log('Copied successfully, showing toast...');
      toast.success('Coupon copied to clipboard! (Local update only)');
      // toast.success('Coupon copied to clipboard! (Local update only)');
    } catch (error) {
      console.error('Failed to copy text:', error);
      toast.error('Failed to copy coupon to clipboard!');
    }
  }

  useEffect(() => {
    fetchUserCoupons();
  }, []);


  const handleCopyCoupon = async (coupon) => {
    console.log(coupon)
    try {
      const token = localStorage.getItem('Startuptoken');
      console.log(token)

      if (!token) {
        toast.error('Authentication required');
        return;
      }

      // Try different API endpoints for marking coupon as used
      let response;
      try {
        // First try the specific endpoint
        response = await axios.patch(
          `${process.env.REACT_APP_API_URL}/api/coupons/${coupon._id}/mark-used`,
          {},
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
      } catch (firstError) {
        console.log('First endpoint failed, trying alternative:', firstError);

        // If that fails, try a different endpoint structure
        try {
          response = await axios.patch(
            `${process.env.REACT_APP_API_URL}/api/coupons/mark-used`,
            {
              couponId: coupon._id,
              code: coupon.code
            },
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );
        } catch (secondError) {
          console.log('Both endpoints failed, updating UI only:', secondError);

          // Even if API fails, update UI and copy to clipboard
          setCoupons(prev => prev.map(c =>
            c._id === coupon._id ? { ...c, used: true, status: 'used' } : c
          ));

          navigator.clipboard.writeText(coupon.code);
          toast.success('Coupon copied to clipboard! (Local update only)');
          return;
        }
      }

      if (response.data && response.data.success) {
        // Update local state to mark coupon as used
        setCoupons(prev => prev.map(c =>
          c._id === coupon._id ? { ...c, used: true, status: 'used' } : c
        ));
        fetchUserCoupons()

        // Copy to clipboard
        navigator.clipboard.writeText(coupon.code);
        toast.success('Coupon copied to clipboard!');
      } else {
        console.error('Failed to mark coupon as used:', response.data);
        toast.error('Failed to mark coupon as used');
      }
    } catch (error) {
      console.error('Error marking coupon as used:', error);

      // Even if there's an error, update UI and copy to clipboard
      setCoupons(prev => prev.map(c =>
        c._id === coupon._id ? { ...c, used: true, status: 'used' } : c
      ));

      navigator.clipboard.writeText(coupon.code);
      toast.success('Coupon copied to clipboard! (Local update)');

      if (error.response) {
        console.error('Server error response:', error.response.data);
      } else if (error.request) {
        console.error('No response received:', error.request);
      } else {
        console.error('Error:', error.message);
      }
    }
  };

  const confirmCopyCoupon = () => {
    if (selectedCoupon) {
      // Mark coupon as used
      setCoupons(prev => prev.map(c =>
        c.id === selectedCoupon.id ? { ...c, used: true } : c
      ));

      // Copy to clipboard
      navigator.clipboard.writeText(selectedCoupon.code);

      // Close modals
      setShowConfirmationModal(false);
      setSelectedCoupon(null);

      toast.success('Coupon copied to clipboard!');
    }
  };

  const handlePitchDeckUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      setIsUploadingPitchDeck(true);
      const uploadResponse = await uploadFile(file);
      setPitchDeckUrl(uploadResponse.url);
      toast.success('Pitch deck uploaded successfully!');
    } catch (error) {
      console.error('Pitch deck upload failed:', error);
      toast.error('Pitch deck upload failed. Please try again.');
    } finally {
      setIsUploadingPitchDeck(false);
    }
  };

  const onGenerateCoupenClick = () => {
    const coupen = coupons.find((each) => each.status == "allocated")
    console.log(coupen)
    if (!coupen && isQuotaExceeded) {
      toast.error("This month quota exceeded!")
    }
    handleCopyCoupon(coupen)

  }

  useEffect(() => {
    if (showDocumentationModal) {
      fetchDynamicFields();
    }
  }, [showDocumentationModal]);

  const fetchDynamicFields = async () => {
    try {
      setLoadingDynamicFields(true);
      const response = await dynamicFieldsAPI.getAllDynamicFields();
      setDynamicFields(response.fields || []);
    } catch (error) {
      console.error('Error fetching dynamic fields:', error);
      toast.error('Failed to load additional fields');
    } finally {
      setLoadingDynamicFields(false);
    }
  };

  // Handle dynamic field input changes
  const handleDynamicFieldChange = (fieldName, value) => {
    setDocumentationForm(prev => ({
      ...prev,
      dynamicFields: {
        ...prev.dynamicFields,
        [fieldName]: value
      }
    }));
  };

  const handleDynamicFileUpload = async (fieldName, file) => {
    if (!file) return;

    try {
      setIsUploading(true);
      const uploadResponse = await uploadFile(file);

      // Store the file URL in the dynamicFields object
      setDocumentationForm(prev => ({
        ...prev,
        dynamicFields: {
          ...prev.dynamicFields,
          [fieldName]: uploadResponse.url
        }
      }));

      toast.success('File uploaded successfully!');
    } catch (error) {
      console.error('File upload failed:', error);
      toast.error('File upload failed. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleDocumentationSubmit = async () => {
    if (!pitchDeckUrl) {
      toast.warning('Please upload a pitch deck first');
      return;
    }

    // Check if any required dynamic fields are missing
    const missingRequiredFields = dynamicFields
      .filter(field => field.required && field.active)
      .filter(field => !documentationForm.dynamicFields[field.fieldName]);

    if (missingRequiredFields.length > 0) {
      toast.warning(`Please fill in all required fields: ${missingRequiredFields.map(f => f.label).join(', ')}`);
      return;
    }

    try {
      setIsSubmitting(true);

      const documentationData = {
        userId: localStorage.getItem('startupId'),
        startupName: startupData?.startupName || '',
        email: startupData?.email || '',
        phone: startupData?.phone || '',
        industry: startupData?.industry || '',
        location: startupData?.location || '',
        pitchDeck: pitchDeckUrl,
        route: 'general',
        dynamicFields: documentationForm.dynamicFields
      };

      const response = await createDocumentation(documentationData);
      console.log('Documentation created successfully:', response);

      toast.success('Documentation submitted successfully!');
      setShowDocumentationModal(false);
      setPitchDeckUrl('');
      setDocumentationForm(prev => ({ ...prev, dynamicFields: {} }));
    } catch (error) {
      console.error('Error submitting documentation:', error);
      toast.error(error.message || 'Failed to submit documentation');
    } finally {
      setIsSubmitting(false);
    }
  };
  const initialHrData = {
    companyName: "",
    GSTno: "",
    email: "",
    phone: "",
    contactperson: "",
    companyAddress: "",
    website: "",
    jobTitle: "",
    department: "",
    postions: "",
    jobType: "",
    jobLocation: "",
    workingDays: "",
    workingHours: "",
    salary: "",
    incentives: "",
    benefits: [],
    skills: [],
    qualification: "",
    experience: "",
    languages: "",
    gender: "",
    age: "",
    additionalNote: ""
  };

  const startupId2 = localStorage.getItem("startupId");

  const [errors, setErrors] = useState({});

  const { data: startupProfileData } = useQuery({
    queryKey: ["startup-profile", startupId2],
    queryFn: async () => {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/startups/${startupId2}`
      );
      return response.data;
    },
    enabled: !!startupId2,
    staleTime: 1000 * 60 * 5,
    cacheTime: 1000 * 60 * 30,
  });

  const onSendHrApplication = async () => {
    setIsSubmitting(true);

    const newErrors = {};

    if (!hrData.companyName) newErrors.companyName = "Company Name is required";
    if (!hrData.GSTno) newErrors.GSTno = "GST Number is required";
    if (!hrData.email) newErrors.email = "Email is required";
    if (!hrData.phone) newErrors.phone = "Phone Number is required";
    if (!hrData.jobTitle) newErrors.jobTitle = "Job Title is required";
    if (!hrData.jobLocation) newErrors.jobLocation = "Job Location is required";

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setIsSubmitting(false);
      return;
    }

    try {
      const response = await createHRapplication(hrData);
      toast.success(response.message || "Application submitted successfully!");

      setHrData(initialHrData);
      setErrors({});
      setShowHrModal(false);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Something went wrong!");
    } finally {
      setIsSubmitting(false);
    }
  };


  useEffect(() => {
    if (startupProfileData) {
      setHrData({ ...hrData, email: startupProfileData.email, contactperson: startupProfileData.fullName, companyName: startupProfileData.startupName, jobLocation: startupData.location, website: startupData.website, phone: startupData.phone, companyAddress: startupData.address, companyID: startupData._id })
    }
    console.log('Startup profile data', startupProfileData)
  }, [startupProfileData])

  const renderDynamicFields = () => {
    if (loadingDynamicFields) {
      return (
        <div className="pt-4">
          <div className="animate-pulse space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-12 bg-gray-200 rounded-md"></div>
            ))}
          </div>
        </div>
      );
    }

    if (dynamicFields.length === 0) {
      return null;
    }

    return (
      <div>
        <div className="space-y-4">
          {dynamicFields
            .filter(field => field.active) // Only show active fields
            .map((field) => (
              <div key={field._id} className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  {field.label || field.fieldName}
                  {field.required && <span className="text-red-500 ml-1">*</span>}
                </label>

                {field.fieldType === 'textarea' ? (
                  <textarea
                    value={documentationForm.dynamicFields[field.fieldName] || ''}
                    onChange={(e) => handleDynamicFieldChange(field.fieldName, e.target.value)}
                    placeholder={field.placeholder || ''}
                    required={field.required}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows={3}
                  />
                ) : field.fieldType === 'select' && field.options ? (
                  <select
                    value={documentationForm.dynamicFields[field.fieldName] || ''}
                    onChange={(e) => handleDynamicFieldChange(field.fieldName, e.target.value)}
                    required={field.required}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select {field.label}</option>
                    {field.options.map((option, index) => (
                      <option key={index} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                ) : field.fieldType === 'file' ? (
                  <div>
                    <input
                      type="file"
                      onChange={(e) => handleDynamicFileUpload(field.fieldName, e.target.files[0])}
                      required={field.required}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                    />
                    {documentationForm.dynamicFields[field.fieldName] && (
                      <p className="text-xs text-green-600 mt-1">File uploaded successfully</p>
                    )}
                  </div>
                ) : field.fieldType === 'checkbox' ? (
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={documentationForm.dynamicFields[field.fieldName] || false}
                      onChange={(e) => handleDynamicFieldChange(field.fieldName, e.target.checked)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label className="ml-2 block text-sm text-gray-700">
                      {field.placeholder || field.label}
                    </label>
                  </div>
                ) : (
                  <input
                    type={field.fieldType === 'number' ? 'number' : 'text'}
                    value={documentationForm.dynamicFields[field.fieldName] || ''}
                    onChange={(e) => handleDynamicFieldChange(field.fieldName, e.target.value)}
                    placeholder={field.placeholder || ''}
                    required={field.required}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                )}
              </div>
            ))}
        </div>
      </div>
    );
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      setIsUploading(true);
      console.log('Uploading selected file...');
      const uploadResponse = await uploadFile(file);
      console.log('File upload complete:', uploadResponse);

      setCloudCreditsForm(prev => ({
        ...prev,
        dpittCertificate: file,
      }));
    } catch (error) {
      console.error('File upload failed:', error);
      alert('File upload failed. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleCloudCreditsSubmit = async () => {
    // Validate required fields
    const requiredFields = [
      'organisationLegalName',
      'registeredAddress',
      'spocName',
      'spocEmail',
      'spocPhone',
      'organisationAge',
      'dpittCertificate' // Now checking for the uploaded path
    ];

    const missingFields = requiredFields.filter(field => !cloudCreditsForm[field]);

    if (missingFields.length > 0) {
      alert(`Missing required fields: ${missingFields.join(', ')}`);
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(cloudCreditsForm.spocEmail)) {
      alert('Please enter a valid email address');
      return;
    }

    // Check if file was uploaded
    if (!cloudCreditsForm.dpittCertificate) {
      alert('File upload is required - please select and wait for file to upload');
      return;
    }

    try {
      setIsSubmitting(true);

      // Prepare the application data
      const applicationData = {
        userId: localStorage.getItem('startupId'),
        startupId: localStorage.getItem('startupId'),
        organisationLegalName: cloudCreditsForm.organisationLegalName,
        registeredAddress: cloudCreditsForm.registeredAddress,
        dpittCertificate: cloudCreditsForm.dpittCertificate,
        spocName: cloudCreditsForm.spocName,
        spocEmail: cloudCreditsForm.spocEmail,
        spocPhone: cloudCreditsForm.spocPhone,
        organisationAge: cloudCreditsForm.organisationAge,
        hasAvailedCredits: cloudCreditsForm.hasAvailedCredits || false,
        cloudProvider: cloudCreditsForm.cloudProvider || '',
        currentlyOnCredits: cloudCreditsForm.currentlyOnCredits || false,
        creditsRemaining: cloudCreditsForm.creditsRemaining || '',
        receivedFunding: cloudCreditsForm.receivedFunding || false,
        fundingAmount: cloudCreditsForm.fundingAmount || ''
      };

      console.log('Submitting application data:', applicationData);
      const response = await createCloudCreditApplication(applicationData);
      console.log('Application submitted successfully:', response);

      // Show success and reset form
      alert('Application submitted successfully!');
      setShowCloudCreditsModal(false);
      resetForm();

    } catch (error) {
      console.error('Error submitting application:', error);

      // Handle different error types
      if (error.response) {
        // Backend returned an error response
        const errorMsg = error.response.data?.error || error.response.data?.message || 'Submission failed';
        alert(`Error: ${errorMsg}`);
      } else if (error.request) {
        // Request was made but no response received
        alert('Network error - please check your connection');
      } else {
        // Something else happened
        alert('An unexpected error occurred');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Helper function to reset form
  const resetForm = () => {
    setCloudCreditsForm({
      organisationLegalName: "",
      registeredAddress: "",
      dpittCertificate: null,
      spocName: "",
      spocEmail: "",
      spocPhone: "",
      organisationAge: "",
      hasAvailedCredits: false,
      cloudProvider: "",
      currentlyOnCredits: false,
      creditsRemaining: "",
      receivedFunding: false,
      fundingAmount: ""
    });
  };

  const startupId = localStorage.getItem("startupId");

  const { data: startupData } = useQuery({
    queryKey: ["startup-profile", startupId],
    queryFn: async () => {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/startups/${startupId}`
      );
      return response.data;
    },
    enabled: !!startupId,
    staleTime: 1000 * 60 * 5,
    cacheTime: 1000 * 60 * 30,
  });

  const { data: queryStatsData, isLoading: statsLoading } = useQuery({
    queryKey: ['dashboardStats'],
    queryFn: fetchStatsData,
    staleTime: 5 * 60 * 1000,
  });

  const { data: queryRecentActivities = [], isLoading: activitiesLoading, refetch: refetchActivities } = useQuery({
    queryKey: ['recentActivities', 4],
    queryFn: () => fetchActivities(4),
    staleTime: 2 * 60 * 1000,
  });

  const SkeletonLoader = ({ className }) => (
    <div className={`animate-pulse bg-gray-200 rounded-md ${className}`}></div>
  );

  useEffect(() => {
    if (queryStatsData) {
      setStatsData({
        ...queryStatsData,
        loading: false
      });
    }
  }, [queryStatsData]);

  const handleMeetingScheduled = () => {
    refetchActivities();
  };

  console.log("Stored token:", localStorage.getItem("Startuptoken"));
  console.log("isGuest:", isGuest());

  const isValidGuest = isGuest()

  const stats = [
    {
      title: "Total Revenue",
      value: "₹0",
      icon: IndianRupee,
      color: "from-green-500 to-emerald-600",
      bgColor: "from-green-50 to-emerald-50",
      requiresCRM: true
    },
    {
      title: "Active Founders",
      value: statsLoading ? "Loading..." : statsData?.founders.toLocaleString() || "0",
      icon: Users,
      color: "from-blue-500 to-cyan-600",
      bgColor: "from-blue-50 to-cyan-50",
    },
    {
      title: "Job Applications",
      value: statsLoading ? "Loading..." : statsData?.applications.toLocaleString() || "0",
      icon: FileText,
      color: "from-purple-500 to-violet-600",
      bgColor: "from-purple-50 to-violet-50",
    },
    {
      title: "Funding Requests",
      value: statsLoading ? "Loading..." : statsData?.fundingRequests?.toLocaleString() || "0",
      icon: TrendingUp,
      color: "from-orange-500 to-red-600",
      bgColor: "from-orange-50 to-red-50",
    },
  ];


  const quickActions = [
    // {
    //   title: "Job Portal",
    //   description: "Start a new job application",
    //   icon: Users,
    //   color: "from-blue-200 to-blue-300",
    //   href: "/job-portal",
    // },
    {
      title: "List for Funding",
      description: "List your Startup for Investment",
      icon: TrendingUp,
      color: "from-green-200 to-green-300",
      href: "/funding",
    },
    {
      title: "Schedule Mentorship",
      description: "Book appointments with advisors",
      icon: Calendar,
      color: "from-purple-200 to-purple-300",
      onClick: () => !isValidGuest ? setShowMeetingModal(true) : navigate("/login"),
    },
    {
      title: "Tech & Marketing Support",
      description: "Safeguarding your Tech Products from scammers by being a trusted intermediary with 0% commissions",
      icon: Zap,
      color: "from-orange-200 to-orange-300",
      href: "/solutions",
    },
  ];

  const comingSoonCards = [
    {
      title: "CA Compliance",
      description: "Get upto 40% discounts on Company registrations IT filing, Annual filing, GST and etc",
      icon: AlertCircle,
      color: "from-purple-200 to-purple-300",
      comingSoon: false,
      href: "/ca-portal"
    },
    {
      title: "Payment Gateways",
      description: "Get Discounted Payment Gateway Integration",
      icon: IndianRupee,
      color: "from-blue-200 to-blue-300",
      comingSoon: false,
      href: "/payment-gateways"
    },
    {
      title: "Cloud Credits",
      description: "Acquire AWS and Google Credits",
      icon: Cloud,
      color: "from-orange-200 to-orange-300",
      comingSoon: false,
      onClick: () => isValidGuest ? navigate("/login") : setShowCloudCreditsModal(true)
    },
    {
      title: "Documentation",
      description: "Comprehensive business documentation",
      icon: Files,
      color: "from-blue-200 to-blue-300",
      comingSoon: false,
      onClick: () => !isValidGuest ? setShowDocumentationModal(true) : navigate("/login"),
    },
    {
      title: "Co-Working Space",
      description: "Identify Co-Working Space Near you",
      icon: FileText,
      color: "from-green-200 to-green-300",
      comingSoon: false,
      href: "/coworkingspaces"

      // countdown: {
      //   targetDate: new Date(Date.now() + 17 * 24 * 60 * 60 * 1000), // 17 days from now
      //   label: "Launching in"
      // }
    },
    {
      title: "Hospitality & Transport",
      description: "Hospitality at its Best",
      icon: Hospital,
      color: "from-orange-200 to-orange-300",
      comingSoon: false, // Changed to false since we're implementing it
      onClick: () => !isValidGuest ? setShowHospitalityModal(true) : navigate("/login")
    },
    {
      title: "HR Management",
      description: "Professional Hiring Solutions",
      icon: ArrowUpRight,
      color: "from-green-200 to-green-300",
      comingSoon: false,
      onClick: () => !isValidGuest ? setShowHrModal(true) : navigate("/login")
      // countdown: {
      //   targetDate: new Date(Date.now() + 17 * 24 * 60 * 60 * 1000), // 35 days from now
      //   label: "Coming in"
      // }
    },
    {
      title: "Insurance and Loans",
      description: "Comprehensive business insurance coverage",
      icon: Landmark,
      color: "from-blue-200 to-blue-300",
      comingSoon: false,
       onClick:()=>"",
      countdown: {
        targetDate: new Date(Date.now() + 60 * 24 * 60 * 60 * 1000), // 35 days from now
        label: "Coming in"
      }
    },
    {
      title: "Incubation",
      description: "Incubation support for startups",
      icon: ArrowUpRight,
      color: "from-green-200 to-green-300",
      comingSoon: false,
     onClick:()=>"",
      countdown: {
        targetDate: new Date(Date.now() + 60 * 24 * 60 * 60 * 1000), // 35 days from now
        label: "Coming in"
      }
    },
    {
      title: "Entrepreneurship Attire",
      description: "Curated line of fashion for Entrepreneurs",
      icon: ArrowDownRight,
      color: "from-purple-200 to-purple-300",
      comingSoon: false,
      onClick:()=>"",
      countdown: {
        targetDate: new Date(Date.now() + 60 * 24 * 60 * 60 * 1000), // 35 days from now
        label: "Coming in"
      }
    },
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case "approved":
      case "completed":
        return "bg-green-100 text-green-800";
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      case "rejected":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "approved":
      case "completed":
        return <CheckCircle className="w-3 h-3" />;
      case "pending":
        return <Clock className="w-3 h-3" />;
      case "rejected":
        return <XCircle className="w-3 h-3" />;
      default:
        return <AlertCircle className="w-3 h-3" />;
    }
  };

  function ActivityDetailsModal({ activity, onClose }) {
    const [copied, setCopied] = useState(false);

    const handleCopy = (text) => {
      navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    };

    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[999] flex items-center justify-center">
        <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
          <div className="p-6 border-b border-gray-200 flex justify-between items-center">
            <h2 className="text-xl font-bold text-gray-900">Activity Details</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          <div className="p-6 space-y-4">
            <div>
              <h3 className="text-lg font-medium text-gray-900">{activity.title}</h3>
              <p className="text-gray-600 mt-1">{activity.description}</p>
            </div>

            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-gray-500">Status</span>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(activity.status)}`}>
                  {getStatusIcon(activity.status)}
                  <span className="ml-1 capitalize">{activity.status}</span>
                </span>
              </div>

              <div className="flex justify-between">
                <span className="text-sm text-gray-500">Time</span>
                <span className="text-sm text-gray-900">{activity.time}</span>
              </div>
            </div>

            {activity.metadata?.meetingLink && (
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Meeting Link</label>
                <div className="flex rounded-md shadow-sm">
                  <input
                    type="text"
                    readOnly
                    value={activity.metadata.meetingLink}
                    className="flex-1 min-w-0 block w-full px-3 py-2 rounded-l-md border border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  />
                  <button
                    onClick={() => handleCopy(activity.metadata.meetingLink)}
                    className="inline-flex items-center px-3 py-2 border border-l-0 border-gray-300 rounded-r-md bg-gray-50 hover:bg-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {copied ? (
                      <CheckCircle className="h-4 w-4 text-green-500" />
                    ) : (
                      <span className="text-xs font-medium">Copy</span>
                    )}
                  </button>
                </div>
              </div>
            )}

            {activity.metadata?.message && (
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
                <div className="p-3 bg-gray-50 rounded-md border border-gray-200">
                  <p className="text-sm text-gray-700">{activity.metadata.message}</p>
                </div>
              </div>
            )}
          </div>

          <div className="p-4 border-t border-gray-200 flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  function HospitalityCouponModal({ coupons, onCopy, onClose, loading, onGenerateClick, onCopy2 }) {
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9999] flex items-center justify-center">
        <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[80vh] overflow-y-auto">
          <div className="p-6 border-b border-gray-200 flex justify-between items-center">
            <h2 className="text-xl font-bold text-gray-900">Hospitality & Transport Coupons</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          <div className="p-6">
            {loading ? (
              <div className="flex justify-center items-center py-8">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
              </div>
            ) : (
              <>
                <div className="mb-6">
                  <p className="text-gray-600">
                    Available discount codes for hospitality and transportation services. Copy a code to use it.
                  </p>
                </div>

                <div className="space-y-4">

                  {/* ✅ If no coupons */}
                  {usedCoupens.length === 0 ? (
                    <div className="text-center py-8">
                      <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-500">No coupons available</p>
                    </div>
                  ) : (
                    usedCoupens.map((coupon) => (
                      <div
                        key={coupon._id}
                        className={`p-4 rounded-lg border ${coupon.used
                            ? 'bg-gray-100 border-gray-300'
                            : 'bg-white border-gray-200 shadow-sm'
                          }`}
                      >
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <h3 className="font-semibold text-gray-900">{coupon.discount}</h3>
                            <p className="text-sm text-gray-600">{coupon.provider}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-xs text-gray-500">
                              {coupon.expires
                                ? `Expires: ${new Date(coupon.expires).toLocaleDateString()}`
                                : 'No expiration'}
                            </p>
                          </div>
                        </div>

                        <div className="flex items-center justify-between mt-3">
                          <code className="bg-gray-100 px-3 py-1 rounded-md text-sm font-mono">
                            {coupon.code}
                          </code>

                          <button
                            onClick={() => onCopy2(coupon)}
                            // disabled={coupon.status=="used"}
                            className={`px-3 py-1 rounded text-xs font-medium ${coupon.used
                                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                : 'bg-blue-600 text-white hover:bg-blue-700'
                              }`}
                          >
                            {coupon.used ? 'Used' : 'Copy Code'}
                          </button>
                        </div>

                        {coupon.used && (
                          <p className="text-xs text-red-500 mt-2">
                            This coupon has been used and is no longer valid.
                          </p>
                        )}
                      </div>
                    ))
                  )}

                  {/* ✅ Show Generate Button centered below coupons */}
                  {usedCoupens.length < 5 && (
                    <div className="flex justify-center py-1">
                      <button
                        onClick={onGenerateClick}
                        className="bg-blue-600 flex items-center gap-4 text-white px-6 py-2 rounded-md font-medium hover:bg-blue-300 transition"
                      >
                        Generate Coupon <FaPlus />
                      </button>
                    </div>
                  )}
                </div>



                <div className="mt-6 pt-4 border-t border-gray-200">
                  <p className="text-sm text-gray-500">
                    Note: Each coupon can only be used once. After copying, the coupon will be marked as used.
                  </p>
                  {isQuotaExceeded && (
                    <p className="text-sm" style={{ color: 'red' }}>This month quota exceeded</p>
                  )}

                </div>
              </>
            )}
          </div>

          <div className="p-4 border-t border-gray-200 flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  function CouponConfirmationModal({ coupon, onConfirm, onCancel }) {
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9999] flex items-center justify-center">
        <div className="bg-white rounded-xl p-6 max-w-sm w-full">
          <div className="text-center">
            <AlertCircle className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Confirm Copy</h3>
            <p className="text-gray-600 mb-4">
              Once you copy the coupon code <strong>{coupon?.code}</strong>, it will be marked as used and cannot be used again.
            </p>

            <div className="flex gap-3 justify-center">
              <button
                onClick={onCancel}
                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={onConfirm}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg"
              >
                Copy Anyway
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const fullName = startupData?.startupName || "";

  return (
    <div className="">
      {/* Welcome Section */}
      <div className="bg-gradient-to-r from-black via-gray-900 to-slate-700 rounded-2xl p-8 text-white relative overflow-hidden">
        {statsLoading ? (
          <div className="relative z-10">
            <SkeletonLoader className="h-8 w-64 mb-4" />
            <SkeletonLoader className="h-6 w-80" />
          </div>
        ) : (
          <>
            <div className="absolute inset-0 bg-black/10" />
            <div className="relative z-10">
              <h1 className="text-3xl font-bold mb-2">Welcome back, {fullName || "User"}!</h1>
              <p className="text-blue-100 text-lg">Here's what's happening with your business today.</p>
            </div>
          </>
        )}
        <div className="absolute -top-4 -right-4 w-32 h-32 bg-white/10 rounded-full blur-2xl" />
        <div className="absolute -bottom-8 -left-8 w-40 h-40 bg-white/5 rounded-full blur-3xl" />
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-3 mb-3">
        {stats.map((stat, index) => {
          const IconComponent = stat.icon;
          const hasAccess = !stat.requiresCRM || statsData?.hasCRM;

          return (
            <div
              key={index}
              className={`relative bg-gradient-to-br ${stat.bgColor} rounded-2xl p-6 border border-white/20 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 group cursor-pointer ${stat.requiresCRM && !hasAccess ? 'overflow-hidden' : ''}`}
            >
              {/* CRM Lock Overlay */}
              {stat.requiresCRM && !hasAccess && (
                <div className="absolute inset-0 bg-white/50 backdrop-blur-lg z-10 flex items-center justify-center p-4">
                  <div className="flex items-center space-x-4">

                    <button
                      onClick={() => {
                        if (isValidGuest) {
                          toast.error("Please Login to access CRM")
                          navigate('/login')

                        } else {
                          navigate('/crm')
                        }
                      }}
                      className="px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors text-sm"
                    >Access CRM</button>
                  </div>
                </div>
              )}

              {/* Main stats row */}
              <div className="flex items-center space-x-4">
                <div className={`p-3 rounded-xl bg-gradient-to-br ${stat.color} shadow-lg transition-transform duration-300 group-hover:scale-110`}>
                  <IconComponent className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-slate-800">{stat.value}</h3>
                  <p className="text-slate-600 text-sm mt-1">{stat.title}</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Chart Section */}
        <div className={`lg:col-span-2 bg-white/30 backdrop-blur-xl rounded-2xl shadow-lg border border-slate-200/60 p-6 relative ${!statsData.hasCRM ? 'overflow-hidden' : ''}`}>
          {!statsData.hasCRM && (
            <>
              <div className="absolute inset-0 bg-white/50 backdrop-blur-lg z-10"></div>
              <div className="absolute inset-0 flex flex-col items-center justify-center z-20 p-4">
                <div className="text-center">
                  <Lock className="w-8 h-8 text-gray-600 mx-auto mb-2" />
                  <p className="text-gray-500 text-sm mb-3">
                    Get Startup CRM at 6000rs per year
                  </p>
                  <button
                    onClick={() => {
                      if (isValidGuest) {
                        toast.error("Please Login to access CRM")
                        navigate('/login')

                      } else {
                        navigate('/crm')
                      }
                    }}
                    className="px-2 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors text-sm"
                  >
                    Access CRM
                  </button>
                </div>
              </div>
            </>
          )}

          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-xl font-bold text-slate-800">Revenue Overview</h2>
              <p className="text-slate-500 text-sm">Monthly performance for the last 6 months</p>
            </div>
            <button className="p-2 hover:bg-slate-100 rounded-lg transition-colors duration-200">
              <MoreHorizontal className="w-5 h-5 text-slate-400" />
            </button>
          </div>

          {statsLoading ? (
            <div className="h-80 flex items-center justify-center">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            </div>
          ) : (
            <div className="h-80 bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl flex items-center justify-center border-2 border-dashed border-slate-300">
              <div className="text-center">
                <div className="text-4xl mb-4">📊</div>
                <p className="text-slate-600 font-medium">Revenue Chart</p>
                <p className="text-slate-400 text-sm">Interactive chart will be displayed here</p>
              </div>
            </div>
          )}
          {/* <RevenueOverview statsLoading={statsLoading} /> */}

        </div>

        {/* Activity status */}
        <div className="bg-white/80 backdrop-blur-xl rounded-2xl shadow-lg border border-slate-200/60 p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-xl font-bold text-slate-800">Recent Activity</h2>
              <p className="text-slate-500 text-sm">Latest updates and notifications</p>
            </div>
            <button
              onClick={() => {
                if (isGuest()) {
                  toast.error("Please Login in to view activities")
                  navigate('/login')
                } else {
                  queryClient.invalidateQueries(['recentActivities']);
                  setShowAllActivities(true);
                }

              }}
              className="text-blue-600 hover:text-blue-700 text-sm font-medium flex items-center space-x-1 hover:scale-105 transition-all duration-200"
            >
              <Eye className="w-4 h-4" />
              <span>View All</span>
            </button>
          </div>

          {activitiesLoading ? (
            <div className="space-y-4">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="flex items-start space-x-4 p-3 rounded-xl">
                  <SkeletonLoader className="w-10 h-10 rounded-lg" />
                  <div className="flex-1 space-y-2">
                    <SkeletonLoader className="h-4 w-3/4" />
                    <SkeletonLoader className="h-3 w-full" />
                    <div className="flex justify-between">
                      <SkeletonLoader className="h-3 w-1/4" />
                      <SkeletonLoader className="h-3 w-1/5" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) :isGuest()?(
             <div className="text-center py-8">
              <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                <AlertCircle className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-700">Please Login to view activities</h3>
              {/* <p className="text-gray-500 mt-1">Your recent activities will appear here</p> */}
            </div>
          ) : queryRecentActivities.length === 0 ? (
            <div className="text-center py-8">
              <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                <AlertCircle className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-700">No recent activity found</h3>
              <p className="text-gray-500 mt-1">Your recent activities will appear here</p>
            </div>
          ) : (
            <div className="space-y-4">
              {queryRecentActivities.map((activity) => (
                <div
                  key={activity.id}
                  onClick={() => setSelectedActivity(activity)}
                  className="flex items-start space-x-4 p-3 rounded-xl hover:bg-slate-50/80 transition-colors duration-200 cursor-pointer"
                >
                  <div className="w-10 h-10 bg-gradient-to-r from-black via-gray-900 to-slate-700 rounded-lg flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                    {activity.avatar}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-800 mb-1">{activity.title}</p>
                    <p className="text-xs text-slate-500 mb-2">{activity.description}</p>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-slate-400">{activity.time}</span>
                      <span
                        className={`inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(activity.status)}`}
                      >
                        {getStatusIcon(activity.status)}
                        <span className="capitalize">{activity.status}</span>
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* All Activities Modal */}
      {showAllActivities && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[80vh] flex flex-col">
            <div className="p-6 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-900">All Activities</h2>
              <button
                onClick={() => setShowAllActivities(false)}
                className="text-gray-400 hover:text-gray-500"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              {activitiesLoading ? (
                <div className="space-y-4">
                  {[...Array(5)].map((_, i) => (
                    <div key={i} className="flex items-start space-x-4 p-3 rounded-xl border border-gray-100">
                      <SkeletonLoader className="w-10 h-10 rounded-lg" />
                      <div className="flex-1 space-y-2">
                        <SkeletonLoader className="h-4 w-3/4" />
                        <SkeletonLoader className="h-3 w-full" />
                        <div className="flex justify-between">
                          <SkeletonLoader className="h-3 w-1/4" />
                          <SkeletonLoader className="h-3 w-1/5" />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : queryRecentActivities.length === 0 ? (
                <div className="text-center py-8">
                  <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                    <AlertCircle className="w-8 h-8 text-gray-400" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-700">No activities found</h3>
                  <p className="text-gray-500 mt-1">Your activities will appear here</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {queryRecentActivities.map((activity) => (
                    <div
                      key={activity.id}
                      className="flex items-start space-x-4 p-3 rounded-xl hover:bg-slate-50/80 transition-colors duration-200 cursor-pointer border border-gray-100"
                    >
                      <div className="w-10 h-10 bg-gradient-to-r from-black via-gray-900 to-slate-700 rounded-lg flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                        {activity.avatar}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-800 mb-1">{activity.title}</p>
                        <p className="text-xs text-slate-500 mb-2">{activity.description}</p>
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-slate-400">{activity.time}</span>
                          <span
                            className={`inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(activity.status)}`}
                          >
                            {getStatusIcon(activity.status)}
                            <span className="capitalize">{activity.status}</span>
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="p-4 border-t border-gray-200 flex justify-end">
              <button
                onClick={() => setShowAllActivities(false)}
                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors duration-200"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Consultation Modal */}
      {showMeetingModal && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowMeetingModal(false);
            }
          }}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ duration: 0.2 }}
            className="bg-white rounded-2xl shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto"
          >
            <ScheduleMeetingModal
              onClose={() => setShowMeetingModal(false)}
              onMeetingScheduled={handleMeetingScheduled}
            />
          </motion.div>
        </div>
      )}

      {/* Quick Actions and Coming Soon Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-5">
        {[...quickActions, ...comingSoonCards].map((item, index) => {
          const IconComponent = item.icon;
          const isClickable = !item.comingSoon && (item.href || item.onClick);

          if (isClickable) {
            const Container = item.href ? Link : 'div';
            const containerProps = item.href ? { to: item.href } : { onClick: item.onClick };

            return (
              <Container
                key={index}
                {...containerProps}
                className={`bg-gradient-to-br ${item.color} rounded-2xl p-6 text-black hover:shadow-xl transition-all duration-300 hover:-translate-y-1 cursor-pointer group`}
              >
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-md font-semibold">{item.title}</h3>
                  <div className="p-2 bg-white/50 rounded-lg group-hover:bg-white/70 transition-colors duration-300 group-hover:scale-110">
                    <IconComponent className="w-5 h-5" />
                  </div>
                </div>
                <p className="text-gray-800 text-sm">{item.description}</p>
                {item.countdown && (
                  <CountdownTimer targetDate={item.countdown.targetDate} label={item.countdown.label} offsetDays={10} />
                )}
              </Container>
            );
          }

          return (
            <div
              key={index}
              className={`bg-gradient-to-br ${item.color} rounded-2xl p-6 text-black group`}
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <h3 className="text-md font-semibold">{item.title}</h3>
                  {item.comingSoon && (
                    <div className="inline-flex items-center justify-center px-2 py-1 bg-white/50 rounded-full text-xs font-medium">
                      Coming Soon
                    </div>
                  )}
                </div>
                <div className="p-2 bg-white/50 rounded-lg">
                  <IconComponent className="w-5 h-5" />
                </div>
              </div>
              <p className="text-gray-800 text-sm">{item.description}</p>
              {/* {item.countdown && (
                <CountdownTimer targetDate={item.countdown.targetDate} label={item.countdown.label} offsetDays={10} />
              )} */}
            </div>
          );
        })}
      </div>

      {selectedActivity && (
        <ActivityDetailsModal
          activity={selectedActivity}
          onClose={() => setSelectedActivity(null)}
        />
      )}

      {showCloudCreditsModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-900">Cloud Credits Application</h2>
              <button
                onClick={() => setShowCloudCreditsModal(false)}
                className="text-gray-400 hover:text-gray-500"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Organisation Legal Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Organisation Legal Name
                  </label>
                  <input
                    type="text"
                    value={cloudCreditsForm.organisationLegalName}
                    onChange={(e) => setCloudCreditsForm({ ...cloudCreditsForm, organisationLegalName: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter legal name"
                  />
                </div>

                {/* Registered Address */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Registered Address
                  </label>
                  <input
                    type="text"
                    value={cloudCreditsForm.registeredAddress}
                    onChange={(e) => setCloudCreditsForm({ ...cloudCreditsForm, registeredAddress: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter registered address"
                  />
                </div>

                {/* DPITT Certificate */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    DPITT Certificate
                  </label>
                  <input
                    type="file"
                    onChange={handleFileChange}
                    disabled={isUploading}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    accept=".pdf,.doc,.docx"
                  />
                  {isUploading && (
                    <div className="mt-2 text-sm text-blue-600">Uploading file...</div>
                  )}
                </div>

                {/* SPOC Details */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    SPOC Name
                  </label>
                  <input
                    type="text"
                    value={cloudCreditsForm.spocName}
                    onChange={(e) => setCloudCreditsForm({ ...cloudCreditsForm, spocName: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter SPOC name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    SPOC Email
                  </label>
                  <input
                    type="email"
                    value={cloudCreditsForm.spocEmail}
                    onChange={(e) => setCloudCreditsForm({ ...cloudCreditsForm, spocEmail: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter SPOC email"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    SPOC Phone
                  </label>
                  <input
                    type="tel"
                    value={cloudCreditsForm.spocPhone}
                    onChange={(e) => setCloudCreditsForm({ ...cloudCreditsForm, spocPhone: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter SPOC phone"
                  />
                </div>

                {/* Organisation Age */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Age of Organisation (years)
                  </label>
                  <input
                    type="number"
                    value={cloudCreditsForm.organisationAge}
                    onChange={(e) => setCloudCreditsForm({ ...cloudCreditsForm, organisationAge: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter organisation age"
                    min="0"
                  />
                </div>
              </div>

              {/* Cloud Credits Section */}
              <div className="space-y-4 pt-4 border-t border-gray-200">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="hasAvailedCredits"
                    checked={cloudCreditsForm.hasAvailedCredits}
                    onChange={(e) => setCloudCreditsForm({ ...cloudCreditsForm, hasAvailedCredits: e.target.checked })}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="hasAvailedCredits" className="ml-2 block text-sm text-gray-700">
                    Have you availed any credits in past from OEM like AWS and Google?
                  </label>
                </div>

                {cloudCreditsForm.hasAvailedCredits && (
                  <div className="space-y-4 pl-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Where are they hosted?
                      </label>
                      <select
                        value={cloudCreditsForm.cloudProvider}
                        onChange={(e) => setCloudCreditsForm({ ...cloudCreditsForm, cloudProvider: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Select provider</option>
                        <option value="AWS">AWS</option>
                        <option value="GCP">Google Cloud (GCP)</option>
                        <option value="Azure">Microsoft Azure</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>

                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="currentlyOnCredits"
                        checked={cloudCreditsForm.currentlyOnCredits}
                        onChange={(e) => setCloudCreditsForm({ ...cloudCreditsForm, currentlyOnCredits: e.target.checked })}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="currentlyOnCredits" className="ml-2 block text-sm text-gray-700">
                        Are you currently running on credits?
                      </label>
                    </div>

                    {cloudCreditsForm.currentlyOnCredits && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          What credits are remaining?
                        </label>
                        <input
                          type="text"
                          value={cloudCreditsForm.creditsRemaining}
                          onChange={(e) => setCloudCreditsForm({ ...cloudCreditsForm, creditsRemaining: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Describe remaining credits"
                        />
                      </div>
                    )}
                  </div>
                )}

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="receivedFunding"
                    checked={cloudCreditsForm.receivedFunding}
                    onChange={(e) => setCloudCreditsForm({ ...cloudCreditsForm, receivedFunding: e.target.checked })}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="receivedFunding" className="ml-2 block text-sm text-gray-700">
                    Have you received any funding in last 12 months from VCs?
                  </label>
                </div>

                {cloudCreditsForm.receivedFunding && (
                  <div className="pl-6">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Funding Amount
                    </label>
                    <input
                      type="text"
                      value={cloudCreditsForm.fundingAmount}
                      onChange={(e) => setCloudCreditsForm({ ...cloudCreditsForm, fundingAmount: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter funding amount"
                    />
                  </div>
                )}
              </div>
            </div>

            <div className="p-4 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => setShowCloudCreditsModal(false)}
                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors duration-200"
              >
                Cancel
              </button>
              <button
                onClick={handleCloudCreditsSubmit}
                disabled={isSubmitting}
                className={`px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors duration-200 ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
              >
                {isSubmitting ? 'Submitting...' : 'Submit Application'}
              </button>
            </div>
          </div>
        </div>
      )}


      {showHrModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-900">Hr Application</h2>
              <button
                onClick={() => setShowHrModal(false)}
                className="text-gray-400 hover:text-gray-500"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

                {/* 01. Company Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Company Name *
                  </label>
                  <input
                    onChange={(e) => setHrData({ ...hrData, companyName: e.target.value })}
                    value={hrData.companyName}
                    type="text"
                    name="companyName"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter company name"
                    required
                  />
                  {errors.companyName && (
                    <p className="text-red-500 text-sm mt-1">{errors.companyName}</p>
                  )}
                </div>

                {/* 02. GST Identification Number */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    GST Identification Number *
                  </label>
                  <input
                    onChange={(e) => setHrData({ ...hrData, GSTno: e.target.value })}
                    value={hrData.GSTno}
                    type="text"
                    name="gstNumber"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter GST number"
                    required
                  />
                  {errors.GSTno && (
                    <p className="text-red-500 text-sm mt-1">{errors.GSTno}</p>
                  )}
                </div>

                {/* 03. Email ID */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email ID *
                  </label>
                  <input
                    onChange={(e) => setHrData({ ...hrData, email: e.target.value })}
                    value={hrData.email}
                    type="email"
                    name="email"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter official email ID"
                    required
                  />
                  {errors.email && (
                    <p className="text-red-500 text-sm mt-1">{errors.email}</p>
                  )}
                </div>

                {/* 04. Phone Number */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Phone Number *
                  </label>
                  <input
                    onChange={(e) => setHrData({ ...hrData, phone: e.target.value })}
                    value={hrData.phone}
                    type="tel"
                    name="phone"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter phone number"
                    required
                  />
                  {errors.phone && (
                    <p className="text-red-500 text-sm mt-1">{errors.phone}</p>
                  )}
                </div>

                {/* 05. Contact Person */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Contact Person
                  </label>
                  <input
                    onChange={(e) => setHrData({ ...hrData, contactperson: e.target.value })}
                    value={hrData.contactperson}
                    type="text"
                    name="contactPerson"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter contact person name"
                  />
                </div>

                {/* 06. Company Address */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Company Address
                  </label>
                  <input
                    onChange={(e) => setHrData({ ...hrData, companyAddress: e.target.value })}
                    value={hrData.companyAddress}
                    type="text"
                    name="companyAddress"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter full address"
                  />
                </div>

                {/* 07. Website */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Website
                  </label>
                  <input
                    onChange={(e) => setHrData({ ...hrData, website: e.target.value })}
                    value={hrData.website}
                    type="url"
                    name="website"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="https://example.com"
                  />
                </div>

                {/* 08. Job Title / Designation */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Job Title / Designation *
                  </label>
                  <input
                    onChange={(e) => setHrData({ ...hrData, jobTitle: e.target.value })}
                    value={hrData.jobTitle}
                    type="text"
                    name="jobTitle"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter job title"
                    required
                  />
                  {errors.jobTitle && (
                    <p className="text-red-500 text-sm mt-1">{errors.jobTitle}</p>
                  )}
                </div>

                {/* 09. Department / Domain */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Department / Domain *
                  </label>
                  <input
                    onChange={(e) => setHrData({ ...hrData, department: e.target.value })}
                    value={hrData.department}
                    type="text"
                    name="department"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="E.g. Development, HR, Marketing"
                    required
                  />
                </div>

                {/* 10. Number of Positions */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Number of Positions *
                  </label>
                  <input
                    onChange={(e) => setHrData({ ...hrData, postions: e.target.value })}
                    value={hrData.postions}
                    type="number"
                    name="positions"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter number of openings"
                    required
                  />
                </div>

                {/* 11. Job Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Job Type *
                  </label>
                  <select
                    onChange={(e) => setHrData({ ...hrData, jobType: e.target.value })}
                    value={hrData.jobType} defaultValue={''}
                    name="jobType"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="">Select job type</option>
                    <option value="Full-time">Full-time</option>
                    <option value="Part-time">Part-time</option>
                    <option value='Internship'>Internship</option>
                    <option value='Contract-based'>Contract-based</option>
                  </select>
                </div>

                {/* 12. Job Location */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Job Location *
                  </label>
                  <input
                    onChange={(e) => setHrData({ ...hrData, jobLocation: e.target.value })}
                    value={hrData.jobLocation}
                    type="text"
                    name="jobLocation"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="On-site / Remote / Hybrid"
                    required
                  />
                  {errors.jobLocation && (
                    <p className="text-red-500 text-sm mt-1">{errors.jobLocation}</p>
                  )}
                </div>

                {/* 13. Working Days */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Working Days
                  </label>
                  <input
                    onChange={(e) => setHrData({ ...hrData, workingDays: e.target.value })}
                    value={hrData.workingDays}
                    type="text"
                    name="workingDays"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="5 or 6 days per week"
                  />
                </div>

                {/* 14. Working Hours */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Working Hours / Shift Timings
                  </label>
                  <input
                    onChange={(e) => setHrData({ ...hrData, workingHours: e.target.value })}
                    value={hrData.workingHours}
                    type="text"
                    name="workingHours"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="E.g. 9 AM - 6 PM"
                  />
                </div>

                {/* 15. Salary Offered */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Salary Offered (CTC / Stipend)
                  </label>
                  <input
                    onChange={(e) => setHrData({ ...hrData, salary: e.target.value })}
                    value={hrData.salary}
                    type="text"
                    name="salary"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="E.g. ₹5 LPA or ₹15,000/month"
                  />
                </div>

                {/* 16. Incentives / Bonuses */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Performance Incentives / Bonuses
                  </label>
                  <input
                    onChange={(e) => setHrData({ ...hrData, incentives: e.target.value })}
                    value={hrData.incentives}
                    type="text"
                    name="incentives"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="E.g. Annual bonus, performance rewards"
                  />
                </div>

                {/* 17. Benefits */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Benefits (selectable options)
                  </label>
                  <div className="flex flex-wrap gap-4">
                    {['Accommodation', 'Travel / Bus facility', 'Food', 'Insurance'].map((benefit) => (
                      <label key={benefit} className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          name="benefits"
                          value={'benefit'}
                          checked={hrData.benefits.includes(benefit)} // keep state synced
                          onChange={(e) => {
                            const isChecked = e.target.checked;
                            setHrData((prev) => {
                              let updatedBenefits = [...prev.benefits];

                              if (isChecked) {
                                // add to array if not already there
                                if (!updatedBenefits.includes(benefit)) {
                                  updatedBenefits.push(benefit);
                                }
                              } else {
                                // remove if unchecked
                                updatedBenefits = updatedBenefits.filter((b) => b !== benefit);
                              }

                              return { ...prev, benefits: updatedBenefits };
                            });
                          }}
                        />
                        <span>{benefit}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* 18. Preferred Qualification */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Preferred Qualification
                  </label>
                  <input
                    onChange={(e) => setHrData({ ...hrData, qualification: e.target.value })}
                    value={hrData.qualification}
                    type="text"
                    name="qualification"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="E.g. BCA, MCA, B.Tech, etc."
                  />
                </div>

                {/* 19. Required Skills */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Required Skills
                  </label>
                  <input
                    onChange={(e) => setHrData({ ...hrData, skills: e.target.value })}
                    value={hrData.skills}
                    type="text"
                    name="skills"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="E.g. React, Node.js, MongoDB"
                  />
                </div>

                {/* 20. Work Experience */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Work Experience (Min - Max in years)
                  </label>
                  <input
                    onChange={(e) => setHrData({ ...hrData, experience: e.target.value })}
                    value={hrData.experience}
                    type="text"
                    name="experience"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="E.g. 0 - 2 years"
                  />
                </div>

                {/* 21. Languages */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Languages Known
                  </label>
                  <input
                    onChange={(e) => setHrData({ ...hrData, languages: e.target.value })}
                    value={hrData.languages}
                    type="text"
                    name="languages"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="E.g. English, Hindi, Tamil"
                  />
                </div>

                {/* 22. Gender Preference */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Gender Preference (if any)
                  </label>
                  <input
                    onChange={(e) => setHrData({ ...hrData, gender: e.target.value })}
                    value={hrData.gender}
                    type="text"
                    name="gender"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="E.g. Male / Female / Any"
                  />
                </div>

                {/* 23. Age Limit */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Age Limit
                  </label>
                  <input
                    onChange={(e) => setHrData({ ...hrData, age: e.target.value })}
                    value={hrData.age}
                    type="text"
                    name="ageLimit"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="E.g. 18 - 35 years"
                  />
                </div>

                {/* 25. Additional Notes */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Additional Notes or Expectations
                  </label>
                  <textarea
                    onChange={(e) => setHrData({ ...hrData, additionalNote: e.target.value })}
                    value={hrData.additionalNote}
                    name="notes"
                    rows="4"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter any additional details or expectations..."
                  ></textarea>
                </div>
              </div>



            </div>

            <div className="p-4 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => setShowHrModal(false)}
                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors duration-200"
              >
                Cancel
              </button>
              <button
                onClick={onSendHrApplication}
                disabled={isSubmitting}
                className={`px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors duration-200 ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
              >
                {isSubmitting ? 'Submitting...' : 'Submit Application'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showDocumentationModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-900">Submit Documentation</h2>
              <button
                onClick={() => setShowDocumentationModal(false)}
                className="text-gray-400 hover:text-gray-500"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              <div className="space-y-2">
                <h3 className="text-lg font-medium">Startup Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Startup Name</p>
                    <p className="font-medium">{startupData?.startupName || 'Not available'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Industry</p>
                    <p className="font-medium">{startupData?.industry || 'Not available'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Email</p>
                    <p className="font-medium">{startupData?.email || 'Not available'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Phone Number</p>
                    <p className="font-medium">{startupData?.phone || 'Not available'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Location</p>
                    <p className="font-medium">{startupData?.location || 'Not available'}</p>
                  </div>
                </div>
              </div>

              <div className="pt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Upload New Pitch Deck (PDF)*
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="file"
                    onChange={handlePitchDeckUpload}
                    disabled={isUploadingPitchDeck}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    accept=".pdf"
                    required
                  />
                  {isUploadingPitchDeck && (
                    <div className="text-sm text-blue-600">Uploading...</div>
                  )}
                </div>
              </div>

              {/* Render dynamic fields */}
              {renderDynamicFields()}
            </div>

            <div className="p-4 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => setShowDocumentationModal(false)}
                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors duration-200"
              >
                Cancel
              </button>
              <button
                onClick={handleDocumentationSubmit}
                disabled={!pitchDeckUrl || isSubmitting}
                className={`px-4 py-2 bg-blue-600 text-white rounded-lg transition-colors duration-200 ${(!pitchDeckUrl || isSubmitting) ? 'opacity-50 cursor-not-allowed' : 'hover:bg-blue-700'
                  }`}
              >
                {isSubmitting ? 'Submitting...' : 'Submit Documentation'}
              </button>
            </div>
          </div>
        </div>
      )}


      {showHospitalityModal && (
        <HospitalityCouponModal
          coupons={coupons}
          onGenerateClick={onGenerateCoupenClick}
          onCopy={handleCopyCoupon}
          onClose={() => setShowHospitalityModal(false)}
          loading={loadingCoupons}
          onCopy2={onCopyClick}
        />
      )}

      {showConfirmationModal && (
        <CouponConfirmationModal
          coupon={selectedCoupon}
          onConfirm={confirmCopyCoupon}
          onCancel={() => {
            setShowConfirmationModal(false);
            setSelectedCoupon(null);
          }}
        />
      )}
    </div>
  );
}